import React, { useState } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { Icon } from './Icon';
import styles from './Layout.module.css';

const layout = cva(styles.base);

// ── Layout ───────────────────────────────────────────────────────────────────

interface LayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

/**
 * Layout — structural page layout container.
 *
 * Composes with Layout.Header, Layout.Sider, Layout.Content, Layout.Footer.
 *
 * @example
 * <Layout>
 *   <Layout.Header>Header</Layout.Header>
 *   <Layout>
 *     <Layout.Sider>Sidebar</Layout.Sider>
 *     <Layout.Content>Main</Layout.Content>
 *   </Layout>
 *   <Layout.Footer>Footer</Layout.Footer>
 * </Layout>
 */
export function Layout({ children, style, className, ...props }: LayoutProps) {
  const hasSider = React.Children.toArray(children).some(
    (child) => React.isValidElement(child) && (child.type as React.ElementType) === Sider,
  );

  return (
    <div
      className={clsx(layout({ className }), hasSider ? styles.row : styles.column)}
      style={style}
      {...props}
    >
      {children}
    </div>
  );
}

// ── Header ───────────────────────────────────────────────────────────────────

function Header({ children, style, className, ...props }: LayoutProps) {
  return (
    <header
      className={clsx(styles.header, className)}
      style={style}
      {...props}
    >
      {children}
    </header>
  );
}

// ── Footer ───────────────────────────────────────────────────────────────────

function Footer({ children, style, className, ...props }: LayoutProps) {
  return (
    <footer
      className={clsx(styles.footer, className)}
      style={style}
      {...props}
    >
      {children}
    </footer>
  );
}

// ── Content ──────────────────────────────────────────────────────────────────

function Content({ children, style, className, ...props }: LayoutProps) {
  return (
    <main
      className={clsx(styles.content, className)}
      style={style}
      {...props}
    >
      {children}
    </main>
  );
}

// ── Sider ────────────────────────────────────────────────────────────────────

interface SiderProps extends React.HTMLAttributes<HTMLElement> {
  /** Width when expanded */
  width?: number | string;
  /** Width when collapsed */
  collapsedWidth?: number;
  /** Collapsible */
  collapsible?: boolean;
  /** Controlled collapsed state */
  collapsed?: boolean;
  /** Default collapsed */
  defaultCollapsed?: boolean;
  /** Collapse change handler */
  onCollapse?: (collapsed: boolean) => void;
  /** Custom trigger element (null to hide) */
  trigger?: React.ReactNode | null;
  children?: React.ReactNode;
}

/**
 * Layout.Sider — collapsible sidebar panel.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | width | number \| string | 240 | Expanded width |
 * | collapsedWidth | number | 48 | Collapsed width |
 * | collapsible | boolean | false | Enable collapse |
 * | collapsed | boolean | — | Controlled collapsed |
 * | defaultCollapsed | boolean | false | Default collapsed |
 * | onCollapse | (collapsed) => void | — | Collapse handler |
 * | trigger | ReactNode \| null | default | Custom trigger |
 */
function Sider({
  width = 240,
  collapsedWidth = 48,
  collapsible = false,
  collapsed: controlledCollapsed,
  defaultCollapsed = false,
  onCollapse,
  trigger,
  children,
  style,
  className,
  ...props
}: SiderProps) {
  const isControlled = controlledCollapsed !== undefined;
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  const isCollapsed = isControlled ? controlledCollapsed : internalCollapsed;

  const toggle = () => {
    const next = !isCollapsed;
    if (!isControlled) setInternalCollapsed(next);
    onCollapse?.(next);
  };

  const currentWidth = isCollapsed
    ? `${collapsedWidth}px`
    : typeof width === 'number' ? `${width}px` : width;

  return (
    <aside
      className={clsx(styles.sider, className)}
      style={{
        '--sider-width': currentWidth,
        ...style,
      } as React.CSSProperties}
      {...props}
    >
      <div className={styles.siderContent}>{children}</div>
      {collapsible && trigger !== null && (
        <button
          onClick={toggle}
          className={styles.siderTrigger}
        >
          {trigger ??
            (isCollapsed ? <Icon name="chevron_right" size={16} /> : <Icon name="chevron_left" size={16} />)}
        </button>
      )}
    </aside>
  );
}

// ── Splitter ─────────────────────────────────────────────────────────────────
// Defined in Splitter.tsx

// ── Attach sub-components ────────────────────────────────────────────────────

Layout.Header = Header;
Layout.Footer = Footer;
Layout.Content = Content;
Layout.Sider = Sider;
